import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import {
  apiBaseUrl,
  getPaymentReference,
  getTimeline,
  onAddReview,
  onRefundStcPayment,
  onCreateRefund,
  onRecordDisputeCancelled,
  onSendNotification,
  onSendPushNotification,
  transactionLineItems,
  transitionPrivileged,
  fetchTransactionMessages,
  sendTransactionMessage,
  sendTransactionQuote,
} from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
  DEAL_PROCESS_NAME,
  DEAL_PURCHASE_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
  BOOKING_PROCESS_NAME,
} from '../../transactions/transaction';

import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import {
  oneSignalCustomerNotificationTemplets,
  oneSignalMerchantNotificationTemplets,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE,
} from '../../util/types';
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';
import {
  Events,
  mixPanelEventsConstants,
  mixPanelEventsData,
  mixPanelEventsScreens,
} from '../../mixpanel';
import { buildTransactionValueProps } from '../../mixpanel/transactionProps';

const { UUID, Money } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const NEGOTIATED_TRANSACTION_MODEL = 'negotiated';
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const ACCEPT_RESCHEDULE_TRANSITION = 'transition/accept-reschedule';
const DEAL_ACCEPT_RESCHEDULE_TRANSITION = 'transition/deal-accept-reschedule';
const REQUEST_RESCHEDULE_TRANSITION = 'transition/request-reschedule';
// Self-serve reschedule (no merchant approval step) -- server-enforced via
// transition-privileged.js + rescheduleEnforcement.js, unlike the three
// transitions above which the client fires directly against the raw SDK.
const CUSTOMER_RESCHEDULE_TRANSITION = 'transition/customer-reschedule';
const DEAL_CUSTOMER_RESCHEDULE_TRANSITION = 'transition/deal-customer-reschedule';
// Self-serve cancellation -- same server-enforced routing as reschedule
// above (see server/api-util/cancellationEnforcement.js), plus a synchronous
// Tap refund settlement that runs server-side right after the transition
// (server/api-util/cancellationSettlement.js).
const CUSTOMER_CANCEL_TRANSITION = 'transition/customer-cancel';
const DEAL_CUSTOMER_CANCEL_TRANSITION = 'transition/deal-customer-cancel';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/TransactionPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/TransactionPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_SERVICE_TIME_SLOTS_REQUEST =
  'app/TransactionPage/FETCH_SERVICE_TIME_SLOTS_REQUEST';
export const FETCH_SERVICE_TIME_SLOTS_SUCCESS =
  'app/TransactionPage/FETCH_SERVICE_TIME_SLOTS_SUCCESS';
export const FETCH_SERVICE_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_SERVICE_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

export const FETCH_CHILD_TRANSACTIONS_REQUEST =
  'app/TransactionPage/FETCH_CHILD_TRANSACTIONS_REQUEST';
export const FETCH_CHILD_TRANSACTIONS_SUCCESS =
  'app/TransactionPage/FETCH_CHILD_TRANSACTIONS_SUCCESS';
export const FETCH_CHILD_TRANSACTIONS_ERROR = 'app/TransactionPage/FETCH_CHILD_TRANSACTIONS_ERROR';

export const FETCH_DEAL_ITEMS_REQUEST = 'app/TransactionPage/FETCH_DEAL_ITEMS_REQUEST';
export const FETCH_DEAL_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_DEAL_ITEMS_SUCCESS';
export const FETCH_DEAL_ITEMS_ERROR = 'app/TransactionPage/FETCH_DEAL_ITEMS_ERROR';

export const FETCH_STAFF_LISTINGS_REQUEST = 'app/TransactionPage/FETCH_STAFF_LISTINGS_REQUEST';
export const FETCH_STAFF_LISTINGS_SUCCESS = 'app/TransactionPage/FETCH_STAFF_LISTINGS_SUCCESS';
export const FETCH_STAFF_LISTINGS_ERROR = 'app/TransactionPage/FETCH_STAFF_LISTINGS_ERROR';

export const FETCH_SERVICE_REQUEST = 'app/TransactionPage/FETCH_SERVICE_REQUEST';
export const FETCH_SERVICE_SUCCESS = 'app/TransactionPage/FETCH_SERVICE_SUCCESS';
export const FETCH_SERVICE_ERROR = 'app/TransactionPage/FETCH_SERVICE_ERROR';

export const FETCH_TIMELINE_REQUEST = 'app/TransactionPage/FETCH_TIMELINE_REQUEST';
export const FETCH_TIMELINE_SUCCESS = 'app/TransactionPage/FETCH_TIMELINE_SUCCESS';
export const FETCH_TIMELINE_ERROR = 'app/TransactionPage/FETCH_TIMELINE_ERROR';

export const STC_REFUND_REQUEST = 'app/TransactionPage/STC_REFUND_REQUEST';
export const STC_REFUND_SUCCESS = 'app/TransactionPage/STC_REFUND_SUCCESS';
export const STC_REFUND_ERROR = 'app/TransactionPage/STC_REFUND_ERROR';
export const STC_REFUND_CLEAR_ERROR = 'app/TransactionPage/STC_REFUND_CLEAR_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  childTransactionsRef: null,
  fetchChildTxsInProgress: false,
  fetchChildTxsError: null,
  currentPageDealItemIds: [],
  fetchDealItemsError: null,
  fetchDealItemsInProgress: false,
  currentPageStaffIds: [],
  fetchStaffListingsError: null,
  fetchStaffListingsInProgress: false,
  currentPageServiceId: null,
  fetchServiceError: null,
  fetchServiceInProgress: false,
  //service time slots
  serviceMonthlyTimeSlots: {},
  paymentReference: null,
  payment_type: null,
  payment_method: null,
  stcPayRefNum: null,
  stcRefundInProgress: false,
  stcRefundError: null,
  stcRefundResult: null,
  statusTimeline: [],
  fetchTimelineInProgress: false,
  fetchTimelineError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

const resultIds = data => {
  const listings = Array.isArray(data.data) ? data.data : [data.data];
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      if (process.env.NODE_ENV !== 'production')
        console.error('[TransactionPage] Fetch transaction error:', payload);
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      if (process.env.NODE_ENV !== 'production')
        console.error('[TransactionPage] Fetch transitions error:', payload);
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case FETCH_CHILD_TRANSACTIONS_REQUEST:
      return { ...state, fetchChildTxsInProgress: true, fetchChildTxsError: null };
    case FETCH_CHILD_TRANSACTIONS_SUCCESS: {
      const childTransactionsRef =
        payload?.map(result => ({
          id: result?.data?.data?.id,
          type: 'transaction',
        })) || [];
      return { ...state, fetchChildTxsInProgress: false, childTransactionsRef };
    }
    case FETCH_CHILD_TRANSACTIONS_ERROR:
      return { ...state, fetchChildTxsInProgress: false, fetchChildTxsError: payload };

    // For listings(deal_items) in Deal
    case FETCH_DEAL_ITEMS_REQUEST:
      return {
        ...state,
        currentPageDealItemIds: [],
        fetchDealItemsInProgress: true,
        fetchDealItemsError: null,
      };
    case FETCH_DEAL_ITEMS_SUCCESS:
      return {
        ...state,
        fetchDealItemsInProgress: false,
        currentPageDealItemIds: resultIds(payload.data),
      };
    case FETCH_DEAL_ITEMS_ERROR:
      return { ...state, fetchDealItemsInProgress: false, fetchDealItemsError: payload };
    /////////////////////////

    // For staff listings (BookingTimeForm's staff picker)
    case FETCH_STAFF_LISTINGS_REQUEST:
      return {
        ...state,
        currentPageStaffIds: [],
        fetchStaffListingsInProgress: true,
        fetchStaffListingsError: null,
      };
    case FETCH_STAFF_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchStaffListingsInProgress: false,
        currentPageStaffIds: resultIds(payload.data),
      };
    case FETCH_STAFF_LISTINGS_ERROR:
      return { ...state, fetchStaffListingsInProgress: false, fetchStaffListingsError: payload };
    /////////////////////////

    // For service of a booked staff
    case FETCH_SERVICE_REQUEST:
      return {
        ...state,
        currentPageServiceId: null,
        fetchServiceInProgress: true,
        fetchServiceError: null,
      };
    case FETCH_SERVICE_SUCCESS:
      return {
        ...state,
        fetchServiceInProgress: false,
        currentPageServiceId: payload.data?.id,
      };
    case FETCH_SERVICE_ERROR:
      return { ...state, fetchServiceInProgress: false, fetchServiceError: payload };
    /////////////////////////

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    //// fetch service slots
    case FETCH_SERVICE_TIME_SLOTS_REQUEST: {
      const serviceMonthlyTimeSlots = {
        ...state.serviceMonthlyTimeSlots,
        [payload]: {
          ...state.serviceMonthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, serviceMonthlyTimeSlots };
    }
    case FETCH_SERVICE_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const serviceMonthlyTimeSlots = {
        ...state.serviceMonthlyTimeSlots,
        [monthId]: {
          ...state.serviceMonthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, serviceMonthlyTimeSlots };
    }
    case FETCH_SERVICE_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const serviceMonthlyTimeSlots = {
        ...state.serviceMonthlyTimeSlots,
        [monthId]: {
          ...state.serviceMonthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, serviceMonthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case FETCH_TIMELINE_REQUEST:
      return { ...state, fetchTimelineInProgress: true, fetchTimelineError: null };
    case FETCH_TIMELINE_SUCCESS:
      return {
        ...state,
        fetchTimelineInProgress: false,
        statusTimeline: Array.isArray(payload?.status_timeline) ? payload.status_timeline : [],
        paymentReference: payload?.paymentReference ?? null,
        payment_type: payload?.payment_type ?? null,
        payment_method: payload?.payment_method ?? null,
        stcPayRefNum: payload?.stcPayRefNum ?? null,
      };
    case FETCH_TIMELINE_ERROR:
      return {
        ...state,
        fetchTimelineInProgress: false,
        fetchTimelineError: payload,
        statusTimeline: [],
        paymentReference: null,
        payment_method: null,
        stcPayRefNum: null,
      };

    case STC_REFUND_REQUEST:
      return { ...state, stcRefundInProgress: true, stcRefundError: null, stcRefundResult: null };
    case STC_REFUND_SUCCESS:
      return {
        ...state,
        stcRefundInProgress: false,
        stcRefundResult: payload,
        stcRefundError: null,
      };
    case STC_REFUND_ERROR:
      return { ...state, stcRefundInProgress: false, stcRefundError: payload };
    case STC_REFUND_CLEAR_ERROR:
      return { ...state, stcRefundError: null };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = state => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = response => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = e => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = e => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = transitionName => ({ type: TRANSITION_REQUEST, payload: transitionName });
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = e => ({ type: TRANSITION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = e => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = e => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

// service time slots
export const fetchServiceTimeSlotsRequest = monthId => ({
  type: FETCH_SERVICE_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchServiceTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_SERVICE_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchServiceTimeSlotsError = (monthId, error) => ({
  type: FETCH_SERVICE_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

const fetchTimelineRequest = () => ({ type: FETCH_TIMELINE_REQUEST });
const fetchTimelineSuccess = data => ({ type: FETCH_TIMELINE_SUCCESS, payload: data });
const fetchTimelineError = e => ({ type: FETCH_TIMELINE_ERROR, error: true, payload: e });

const stcRefundRequest = () => ({ type: STC_REFUND_REQUEST });
const stcRefundSuccess = data => ({ type: STC_REFUND_SUCCESS, payload: data });
const stcRefundErrorAction = e => ({ type: STC_REFUND_ERROR, error: true, payload: e });
export const clearStcRefundError = () => ({ type: STC_REFUND_CLEAR_ERROR });

export const fetchTimeline = (transactionId, merchantId) => dispatch => {
  if (!transactionId || !merchantId) return Promise.resolve();
  dispatch(fetchTimelineRequest());
  return getTimeline(transactionId, merchantId)
    .then(data => dispatch(fetchTimelineSuccess(data)))
    .catch(e => dispatch(fetchTimelineError(storableError(e))));
};

export const refundStcPayment = ({ paymentId, amount, refundPercentage } = {}) => dispatch => {
  dispatch(stcRefundRequest());
  return onRefundStcPayment({ paymentId, amount, refundPercentage })
    .then(data => {
      dispatch(stcRefundSuccess(data));
      return data;
    })
    .catch(e => {
      dispatch(stcRefundErrorAction(storableError(e)));
      throw e;
    });
};

export const refundTapPayment = ({ chargeId, amount, refundPercentage } = {}) => dispatch => {
  dispatch(stcRefundRequest()); // Reuse STC refund status for simplicity or create TAP-specific one
  return onCreateRefund({ chargeId, amount, refundPercentage })
    .then(data => {
      dispatch(stcRefundSuccess(data));
      return data;
    })
    .catch(e => {
      dispatch(stcRefundErrorAction(storableError(e)));
      throw e;
    });
};

// For Deal's child transactions
export const fetchChildTransactionsRequest = () => ({ type: FETCH_CHILD_TRANSACTIONS_REQUEST });
export const fetchChildTransactionsSuccess = childTxs => ({
  type: FETCH_CHILD_TRANSACTIONS_SUCCESS,
  payload: childTxs,
});
export const fetchChildTransactionsError = error => ({
  type: FETCH_CHILD_TRANSACTIONS_ERROR,
  error: true,
  payload: error,
});

// Action creators for listings in Deal (deal_items)
export const fetchDealItemsRequest = () => ({ type: FETCH_DEAL_ITEMS_REQUEST });
export const fetchDealItemsSuccess = response => ({
  type: FETCH_DEAL_ITEMS_SUCCESS,
  payload: response,
});
export const fetchDealItemsError = error => ({
  type: FETCH_DEAL_ITEMS_ERROR,
  error: true,
  payload: error,
});

// Action creators for staff listings (BookingTimeForm's staff picker)
export const fetchStaffListingsRequest = () => ({ type: FETCH_STAFF_LISTINGS_REQUEST });
export const fetchStaffListingsSuccess = response => ({
  type: FETCH_STAFF_LISTINGS_SUCCESS,
  payload: response,
});
export const fetchStaffListingsError = error => ({
  type: FETCH_STAFF_LISTINGS_ERROR,
  error: true,
  payload: error,
});

// Action creators for service in a booked staff
export const fetchServiceRequest = () => ({ type: FETCH_SERVICE_REQUEST });
export const fetchServiceSuccess = response => ({
  type: FETCH_SERVICE_SUCCESS,
  payload: response,
});
export const fetchServiceError = error => ({
  type: FETCH_SERVICE_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

// Cache key for monthlyTimeSlots/serviceMonthlyTimeSlots. Namespaced by
// listing, not just month: this page renders time-slot calendars for two
// *different* listings (the main service listing via OrderPanel, and the
// staff listing via RescheduleModal) into the same Redux slice. Keying by
// month alone let one listing's fetch silently overwrite the other's slots
// for the same month, so the reschedule calendar could show either an empty
// cache (every day blocked) or the wrong listing's availability, depending
// on fetch order/timing.
const timeSlotsCacheKey = (listingId, start, timeZone) =>
  `${listingId?.uuid || listingId}_${monthIdString(start, timeZone)}`;

export const fetchTimeSlots = (listingId, start, end, timeZone, isService = false) => (
  dispatch,
  getState,
  sdk
) => {
  const monthId = monthIdString(start, timeZone);
  const cacheKey = timeSlotsCacheKey(listingId, start, timeZone);

  isService
    ? dispatch(fetchServiceTimeSlotsRequest(cacheKey))
    : dispatch(fetchTimeSlotsRequest(cacheKey));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      isService
        ? dispatch(fetchServiceTimeSlotsSuccess(cacheKey, timeSlots))
        : dispatch(fetchTimeSlotsSuccess(cacheKey, timeSlots));
    })
    .catch(e => {
      isService
        ? dispatch(fetchServiceTimeSlotsError(cacheKey, storableError(e)))
        : dispatch(fetchTimeSlotsError(cacheKey, storableError(e)));
    });
};

// Fetch staff listings for the negotiated-model booking form shown once an
// offer has been accepted -- mirrors ListingPage.duck.js's fetchStaffListings
// (same listing-availability query), duplicated here since TransactionPage
// has its own Redux slice rather than reaching into ListingPage's.
export const fetchStaffListings = ({ listingId, profileListingId, config, start, end }) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchStaffListingsRequest());

  sdk.listings
    .show({
      id: profileListingId,
    })
    .then(response => {
      const businessProfile = response?.data?.data;
      const staffIds = businessProfile?.attributes?.publicData?.staffId || [];

      sdk.listings
        .query(
          {
            ids: staffIds,
            availability: 'time-full',
            start,
            end,
            pub_serviceIds: `has_any:${listingId?.uuid}`,
            include: ['images'],
            'fields.image': ['variants.square-small', 'variants.square-small2x'],
          },
          {
            expand: true,
          }
        )
        .then(response => {
          const listingFields = config?.listing?.listingFields;
          const sanitizeConfig = { listingFields };
          dispatch(addMarketplaceEntities(response, sanitizeConfig));
          dispatch(fetchStaffListingsSuccess(response));
        });
    })
    .catch(e => {
      dispatch(fetchStaffListingsError(storableError(e)));
    });
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing, isService = false) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const nextBoundary = findNextBoundary(new Date(), 'hour', tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz, isService)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz, isService)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const getNotificationImageQueryParams = () => {
  return {
    'fields.image': [
      'variants.default',
      'variants.square-small',
      'variants.square-small2x',
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.listing-card',
      'variants.listing-card-2x',
    ],
    ...createImageVariantConfig('listing-card', 400, 1),
    ...createImageVariantConfig('listing-card-2x', 800, 1),
  };
};

const listingRelationship = txResponse => {
  return txResponse.data.data.relationships.listing.data;
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());
  let txResponse = null;

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.images',
          'listing.currentStock',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then(response => {
      txResponse = response;
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];
      const processName = resolveLatestProcessName(transaction.attributes.processName);
      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;
        const isAccepted = process.getState(transaction) === process.states.ACCEPTED;
        // Negotiated model: once an offer is accepted (either side), the
        // customer picks a date/time on this page (see negotiatedOfferAccepted
        // in TransactionPage.js) -- without fetching slots here too, the
        // calendar's monthlyTimeSlots stays empty and every day renders as
        // blocked (see isDayBlocked in FieldDateAndTimeInput.js).
        const isOfferAccepted = process.getState(transaction) === process.states.OFFER_ACCEPTED;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && (isAccepted || isOfferAccepted);

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      const canFetchListing = listing && listing.attributes && !listing.attributes.deleted;
      if (canFetchListing) {
        return sdk.listings.show({
          id: listingId,
          include: ['author', 'author.profileImage', 'images'],
          ...getImageVariants(config.layout.listingImage),
        });
      } else {
        return response;
      }
    })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config.layout.listingImage;

      const aspectRatio = aspectHeight / aspectWidth;
      const listing = response?.data?.data;
      const { deal_items = [], listingType } = listing?.attributes?.publicData || {};
      const { serviceId } = txResponse?.data?.data?.attributes?.protectedData || {};

      if (deal_items?.length > 0) {
        dispatch(fetchDealItemsRequest());

        sdk.listings
          .query(
            {
              ids: deal_items,
              include: ['images'],
              'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
              ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
              ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            },
            { expand: true }
          )
          .then(dealItemsResponse => {
            if (dealItemsResponse) {
              const listingFields = config?.listing?.listingFields;
              const sanitizeConfig = { listingFields };

              // fetch service time slots
              const dealItemListing = dealItemsResponse?.data?.data?.[0];
              const isServiceItem =
                dealItemListing?.attributes?.publicData?.listingType === SERVICE_LISTING_TYPE;

              if (dealItemListing && isServiceItem) {
                // console.log("fetch service time slots - deal");
                fetchMonthlyTimeSlots(dispatch, dealItemListing, true);
              }

              dispatch(fetchDealItemsSuccess(dealItemsResponse));
              dispatch(addMarketplaceEntities(dealItemsResponse, sanitizeConfig));
            }
          })
          .catch(e => {
            dispatch(fetchDealItemsError(storableError(e)));
          });
      } else if (listingType === STAFF_LISTING_TYPE && serviceId) {
        dispatch(fetchServiceRequest());
        sdk.listings
          .show(
            {
              id: serviceId,
              include: ['images'],
              'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
              ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
              ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            },
            { expand: true }
          )
          .then(serviceResponse => {
            // fetch service time slots
            const serviceListing = serviceResponse?.data?.data;
            if (serviceListing) {
              // console.log("fetch service time slots - staff");
              fetchMonthlyTimeSlots(dispatch, serviceListing, true);
            }

            if (serviceResponse) {
              const listingFields = config?.listing?.listingFields;
              const sanitizeConfig = { listingFields };
              dispatch(fetchServiceSuccess(serviceResponse?.data));
              dispatch(addMarketplaceEntities(serviceResponse, sanitizeConfig));
            }
          })
          .catch(e => {
            dispatch(fetchServiceError(storableError(e)));
          });
      }

      dispatch(addMarketplaceEntities(txResponse, sanitizeConfig));
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(txResponse));
      return response;
    })
    .then(response => {
      const { dealChildTransactions = [] } =
        txResponse?.data?.data?.attributes?.protectedData || {};

      if (dealChildTransactions.length) {
        dispatch(fetchChildTransactionsRequest());

        const promises = dealChildTransactions.map(childTx =>
          sdk.transactions
            .show({
              id: childTx,
              include: [
                'listing',
                'listing.images',
                'listing.currentStock',
                'customer',
                'customer.profileImage',
                'provider',
                'provider.profileImage',
                'booking',
                'reviews',
                'reviews.author',
                'reviews.subject',
              ],
              ...getImageVariants(config.layout.listingImage),
            })
            .then(res => {
              const listingFields = config?.listing?.listingFields;
              const sanitizeConfig = { listingFields };
              dispatch(addMarketplaceEntities(res, sanitizeConfig));
              return res;
            })
        );

        Promise.all(promises) // Wait for all promises to resolve
          .then(results => {
            dispatch(fetchChildTransactionsSuccess(results));

            if (results?.length > 0) {
              const listingId = listingRelationship(results[0]).id;
              const entities = updatedEntities({}, results[0].data);
              const listingRef = { id: listingId, type: 'listing' };
              const transactionRef = { id: results[0].data?.data?.id, type: 'transaction' };
              const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
              const listing = denormalised[0];
              const transaction = denormalised[1];
              const processName = resolveLatestProcessName(transaction.attributes.processName);
              try {
                const process = getProcess(processName);
                const isAccepted = process.getState(transaction) === process.states.ACCEPTED;

                // Fetch time slots for transactions that are in inquired state
                const canFetchTimeslots = txRole === 'customer' && isBookingProcess(processName);

                if (canFetchTimeslots) {
                  fetchMonthlyTimeSlots(dispatch, listing);
                }
              } catch (error) {
                console.log(`transaction process (${processName}) was not recognized`);
              }
            }
          })
          .catch(e => {
            dispatch(fetchChildTransactionsError(storableError(e)));
            throw e;
          });
      }
      return response;
    })
    .catch(e => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then(response => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch(e => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};
const getOrderType = processName => {
  if (processName == PURCHASE_PROCESS_NAME || processName == DEAL_PURCHASE_PROCESS_NAME)
    return 'Order';
  else return 'Booking';
};
const getLabel = transitionName => {
  switch (transitionName) {
    case 'transition/dispute':
      return 'dispute_raise';
    case 'transition/request-reschedule':
      return 'reschedule_request';
    case 'transition/provider-mark-received-from-disputed':
    case 'transition/received-from-disputed':
      return 'dispute_accepted';
    case 'transition/cancel-from-disputed':
    case 'transition/provider-mark-cancel-from-disputed':
      return 'dispute_declined';
    case 'transition/accept-reschedule':
    case 'transition/deal-accept-reschedule':
      return 'reschedule_approved';
    case 'transition/cancel-reschedule':
      return 'reschedule_denied';
    // Self-serve reschedule: the customer is the actor, so this notifies the
    // merchant (informational -- there's nothing to approve). Distinct label
    // from reschedule_approved even though the outcome reads similarly,
    // since the receiving role is the opposite of the legacy flow.
    case 'transition/customer-reschedule':
    case 'transition/deal-customer-reschedule':
      return 'reschedule_completed';
    // Self-serve cancellation: same reasoning as customer-reschedule above --
    // the customer is the actor, so this notifies the merchant.
    case 'transition/customer-cancel':
      return 'cancellation_completed';

    // Negotiated model. Each transition is named by its actor, so the label
    // alone determines which side receives the push.
    case 'transition/inquire':
      return 'negotiation_quote_requested';
    case 'transition/make-offer-after-inquiry':
    case 'transition/provider-make-counter-offer':
      return 'negotiation_offer_received';
    case 'transition/provider-reject-counter-offer':
      return 'negotiation_counter_declined';
    case 'transition/customer-make-counter-offer':
      return 'negotiation_counter_offer';
    case 'transition/customer-accept-offer':
      return 'negotiation_offer_accepted';
    case 'transition/customer-reject-offer':
      return 'negotiation_offer_declined';
    case 'transition/provider-accept-offer':
      return 'negotiation_counter_accepted';
    case 'transition/provider-withdraw-offer':
      return 'negotiation_offer_withdrawn';
  }
};

/** Not a transition — the chat thread's own send. */
export const NEGOTIATION_MESSAGE_LABELS = {
  fromCustomer: 'negotiation_new_message',
  fromProvider: 'negotiation_message_received',
};

const getTemplate = label => {
  switch (label) {
    case 'dispute_raise':
      return oneSignalMerchantNotificationTemplets?.RAISE_DISPUTE;
    case 'reschedule_request':
      return oneSignalMerchantNotificationTemplets.RESCHEDULE_REQUEST;
    case 'dispute_accepted':
      return oneSignalCustomerNotificationTemplets.ACCEPT_DISPUTE;
    case 'dispute_declined':
      return oneSignalCustomerNotificationTemplets.DECLINE_DISPUTE;
    case 'reschedule_approved':
      return oneSignalCustomerNotificationTemplets.RESCHEDULE_APPROVED;
    case 'reschedule_denied':
      return oneSignalCustomerNotificationTemplets.RESCHEDULE_DENIED;
    case 'reschedule_completed':
      return oneSignalMerchantNotificationTemplets.CUSTOMER_RESCHEDULED;
    case 'cancellation_completed':
      return oneSignalMerchantNotificationTemplets.CUSTOMER_CANCELLED;

    // Negotiated model — to the merchant (the customer acted).
    case 'negotiation_quote_requested':
      return oneSignalMerchantNotificationTemplets.NEGOTIATION_QUOTE_REQUESTED;
    case 'negotiation_counter_offer':
      return oneSignalMerchantNotificationTemplets.NEGOTIATION_COUNTER_OFFER;
    case 'negotiation_offer_accepted':
      return oneSignalMerchantNotificationTemplets.NEGOTIATION_OFFER_ACCEPTED;
    case 'negotiation_offer_declined':
      return oneSignalMerchantNotificationTemplets.NEGOTIATION_OFFER_DECLINED;
    case NEGOTIATION_MESSAGE_LABELS.fromCustomer:
      return oneSignalMerchantNotificationTemplets.NEGOTIATION_NEW_MESSAGE;

    // Negotiated model — to the customer (the merchant acted).
    case 'negotiation_offer_received':
      return oneSignalCustomerNotificationTemplets.NEGOTIATION_OFFER_RECEIVED;
    case 'negotiation_counter_accepted':
      return oneSignalCustomerNotificationTemplets.NEGOTIATION_COUNTER_ACCEPTED;
    case 'negotiation_counter_declined':
      return oneSignalCustomerNotificationTemplets.NEGOTIATION_COUNTER_DECLINED;
    case 'negotiation_offer_withdrawn':
      return oneSignalCustomerNotificationTemplets.NEGOTIATION_OFFER_WITHDRAWN;
    case NEGOTIATION_MESSAGE_LABELS.fromProvider:
      return oneSignalCustomerNotificationTemplets.NEGOTIATION_MESSAGE_RECEIVED;
  }
};

const getImageUrlFromImageEntity = image => {
  if (!image) return '';

  const directUrl = image?.url || image?.attributes?.url || image?.attributes?.originalUrl;
  if (typeof directUrl === 'string' && directUrl.trim().length > 0) {
    return directUrl;
  }

  const variants = image?.attributes?.variants || {};
  const preferredUrl =
    variants?.default?.url ||
    variants?.['listing-card']?.url ||
    variants?.['listing-card-2x']?.url ||
    variants?.['scaled-medium']?.url ||
    variants?.['scaled-small']?.url ||
    variants?.['square-small']?.url;

  if (preferredUrl) {
    return preferredUrl;
  }

  const firstVariantUrl = Object.values(variants).find(variant => variant?.url)?.url;
  return firstVariantUrl || '';
};

const getImageUrlFromListingEntity = listing => {
  const images = listing?.images || listing?.attributes?.images || [];
  if (!Array.isArray(images) || images.length === 0) {
    return '';
  }

  const firstImageWithUrl = images.find(image => !!getImageUrlFromImageEntity(image));
  return getImageUrlFromImageEntity(firstImageWithUrl || images[0]);
};

const getImageUrlFromIncludedListing = (listing, included = []) => {
  const imageRefs = listing?.relationships?.images?.data;
  if (!Array.isArray(imageRefs) || imageRefs.length === 0) return '';

  const firstImageRef = imageRefs[0];
  const firstImageRefId = firstImageRef?.id?.uuid || firstImageRef?.id || null;
  const firstImage = included.find(
    item => item?.type == 'image' && (item?.id?.uuid || item?.id) == firstImageRefId
  );

  return getImageUrlFromImageEntity(firstImage);
};

const createNotificationId = () => `notif-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;

const sendReviewReceivedNotification = ({
  tx,
  listing,
  senderUser,
  receiverUserId,
  reviewRating,
  reviewContent,
  imageUrl = '',
  listingTitle = '',
}) => {
  if (!receiverUserId || !senderUser?.id?.uuid) {
    return Promise.resolve();
  }

  const notificationListing = listing || tx?.listing;
  const listingImageUrl = imageUrl || getImageUrlFromListingEntity(notificationListing);
  const resolvedListingTitle = listingTitle || notificationListing?.attributes?.title || '';
  const safeRating = Number.isFinite(Number(reviewRating)) ? Number(reviewRating) : null;
  const reviewText = String(reviewContent || '').trim();

  
  if (process.env.NODE_ENV !== 'production') {
    console.log('[NotificationDebug][REVIEW_RECEIVED] payload image', {
      transactionId: tx?.id?.uuid,
      receiverUserId,
      image: listingImageUrl || '',
      notificationType: 'REVIEW_RECEIVED',
    });
  }

  return onSendNotification({
    userId: receiverUserId,
    message: {
      notificationId: createNotificationId(),
      notificationType: 'REVIEW_RECEIVED',
      label: reviewText,
      reviewText: reviewText,
      rating: safeRating,
      transactionId: tx?.id?.uuid,
      actionId: notificationListing?.id?.uuid,
      image: listingImageUrl || '',
      itemName: resolvedListingTitle,
    },
  }).catch(error => {
    console.log('review notification failed', error);
  });
};
/**
 * Push for a transition the user just made, to the other party.
 *
 * `extra` carries the fields only some events have — a negotiation push adds
 * the amount and the screen to open, a chat message its preview and a
 * collapse id. Transitions with no template in getTemplate() are skipped
 * outright rather than firing a request the server will reject.
 */
export const sendNotification = async (
  response,
  user,
  transitionName,
  serviceListing,
  extra = {}
) => {
  const included = response?.data?.included || [];
  const senderUser = included.filter(
    item => item.type == 'user' && item.id.uuid == user.id.uuid
  )[0];
  const otherUser = included.filter(item => item.type == 'user' && item.id.uuid != user.id.uuid)[0];
  const listing = included.filter(item => item.type == 'listing')[0];
  const userType =
    senderUser?.attributes?.profile?.publicData?.userType == 'provider' ? 'merchant' : 'customer';
  const orderType = getOrderType(response?.data?.data?.attributes?.processName);
  const label = getLabel(transitionName);
  const isBooking = response?.data?.data?.attributes?.processName === BOOKING_PROCESS_NAME;
  const template = getTemplate(label);
  const listingImageUrl = isBooking
    ? getImageUrlFromListingEntity(serviceListing)
    : getImageUrlFromIncludedListing(listing, included);

  // Most transitions have no push of their own; without this they would fire a
  // request with template_id undefined, which the server rejects as a 400.
  if (!template) {
    return;
  }

  const { collapseId, ...extraCustomData } = extra || {};

  const customData = {
    image: listingImageUrl || '',
    order_type: orderType,
    [userType == 'customer' ? 'customer_name' : 'merchant_name']: senderUser?.attributes?.profile
      ?.displayName,
    item_name: isBooking ? serviceListing?.attributes?.title : listing?.attributes?.title,
    transactionId: response?.data?.data?.id?.uuid,
    label: label,
    ...extraCustomData,
  };

  return onSendPushNotification({
    userRole: userType,
    receiverUserIds: [otherUser?.id?.uuid],
    senderUserId: senderUser?.id?.uuid,
    template_id: template,
    notificationData: customData,
    customData,
    ...(collapseId ? { collapse_id: collapseId } : {}),
  }).catch(e => console.log('sendNotification error', e));
};
const getListing = (state, id) => {
  const ref = { id, type: 'listing' };
  const listings = getMarketplaceEntities(state, [ref]);
  return listings.length === 1 ? listings[0] : null;
};

/**
 * Self-serve reschedule (transition/customer-reschedule /
 * transition/deal-customer-reschedule). Unlike every other transition in
 * makeTransition, these are server-enforced (limit + lead-time, read from
 * the transaction's own protectedData snapshot) so the client can no longer
 * fire them against the raw SDK -- see server/api-util/rescheduleEnforcement.js.
 * Routed through transitionPrivileged the same way
 * fireNegotiationPriceTransition routes the negotiated-pricing transitions.
 *
 * Deal parent/child split (mirrors the legacy DEAL_ACCEPT_RESCHEDULE ->
 * ACCEPT_RESCHEDULE child-loop above): DEAL_CUSTOMER_RESCHEDULE fires on the
 * parent (protectedData-only, no booking of its own) and remaps to
 * CUSTOMER_RESCHEDULE for each deal child, which is what actually carries
 * the real booking and moves its dates.
 */
const runSelfServeReschedule = async (
  { txId, transitionName, params, dealChildTransactionIds = [], serviceListing },
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(transitionRequest(transitionName));

    if (transitionName === DEAL_CUSTOMER_RESCHEDULE_TRANSITION && dealChildTransactionIds.length > 0) {
      await Promise.all(
        dealChildTransactionIds.map(childTxId =>
          transitionPrivileged({
            isSpeculative: false,
            bodyParams: { id: childTxId, transition: CUSTOMER_RESCHEDULE_TRANSITION, params },
            queryParams: { expand: true },
          })
        )
      );
    }

    const response = await transitionPrivileged({
      isSpeculative: false,
      bodyParams: { id: txId, transition: transitionName, params },
      queryParams: {
        include: ['listing', 'listing.images', 'customer', 'provider'],
        ...getNotificationImageQueryParams(),
        expand: true,
      },
    });

    const user = getState()?.user?.currentUser;
    await sendNotification(response, user, transitionName, serviceListing);
    dispatch(addMarketplaceEntities(response));
    dispatch(transitionSuccess());
    dispatch(fetchCurrentUserNotifications());
    refreshTransactionEntity(sdk, txId, dispatch);

    trackEventsMixpanel(Events.CUSTOMER_BOOKING_COMPLETED, {
      action_type: mixPanelEventsData.CUSTOMER_BOOKING_RESCHEDULE,
      screen: mixPanelEventsScreens.TRANSACTION_PAGE,
      user_type: 'customer',
      user_id: user?.id?.uuid,
      ...buildTransactionValueProps(response?.data?.data, {
        role: 'customer',
        listingTitle: serviceListing?.attributes?.title,
      }),
    });

    return response;
  } catch (e) {
    dispatch(transitionError(storableError(e)));
    log.error(e, `${transitionName}-failed`, { txId, transition: transitionName, message: e?.message });
    throw e;
  }
};

/**
 * Self-serve cancellation (transition/customer-cancel /
 * transition/deal-customer-cancel). Routed through transitionPrivileged the
 * same way runSelfServeReschedule is above -- the lead-time cutoff is
 * server-enforced (read from the transaction's own protectedData snapshot),
 * and the synchronous Tap refund settlement only runs from that endpoint
 * (see server/api/transition-privileged.js#handleCancellationTransition).
 *
 * No deal-parent/child split here unlike reschedule: cancellationSettlement.js
 * only ever settles payment for the plain booking / deal-child transaction,
 * never the deal parent (which carries no payment data of its own), so this
 * never fires against dealChildTransactionIds.
 */
const runSelfServeCancellation = async (
  { txId, transitionName, params, serviceListing },
  dispatch,
  getState,
  sdk
) => {
  try {
    dispatch(transitionRequest(transitionName));
    console.log('[Cancellation][Client] Firing transition', { txId: txId?.uuid, transitionName, params });

    const response = await transitionPrivileged({
      isSpeculative: false,
      bodyParams: { id: txId, transition: transitionName, params },
      queryParams: {
        include: ['listing', 'listing.images', 'customer', 'provider'],
        ...getNotificationImageQueryParams(),
        expand: true,
      },
    });

    console.log('[Cancellation][Client] Transition succeeded', {
      txId: txId?.uuid,
      lastTransition: response?.data?.data?.attributes?.lastTransition,
    });

    const user = getState()?.user?.currentUser;
    await sendNotification(response, user, transitionName, serviceListing);
    dispatch(addMarketplaceEntities(response));
    dispatch(transitionSuccess());
    dispatch(fetchCurrentUserNotifications());
    refreshTransactionEntity(sdk, txId, dispatch);
    // Cancellation is tracked once, as transaction_cancelled from CancelModal
    // (matching the mobile app) -- not also as customer_booking_completed.

    return response;
  } catch (e) {
    console.error('[Cancellation][Client] Transition failed', {
      txId: txId?.uuid,
      code: e?.apiErrors?.[0]?.code || e?.data?.errors?.[0]?.code,
      message: e?.message,
    });
    dispatch(transitionError(storableError(e)));
    log.error(e, `${transitionName}-failed`, { txId, transition: transitionName, message: e?.message });
    throw e;
  }
};

export const makeTransition = (
  txId,
  transitionName,
  params,
  intl = {},
  processName,
  dealChildTransactionIds = []
) => async (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }

  const state = getState();
  const txRef = txId ? { id: txId, type: 'transaction' } : null;
  const currentTx = txRef ? getMarketplaceEntities(state, [txRef])?.[0] : null;
  const txServiceId = currentTx?.attributes?.protectedData?.serviceId;
  const serviceId = state.TransactionPage?.currentPageServiceId || txServiceId || null;
  const serviceListing = serviceId ? getListing(state, serviceId) : null;
  if (!processName) {
    return Promise.reject(new Error('processName is required'));
  }

  if ([CUSTOMER_RESCHEDULE_TRANSITION, DEAL_CUSTOMER_RESCHEDULE_TRANSITION].includes(transitionName)) {
    return runSelfServeReschedule(
      { txId, transitionName, params, dealChildTransactionIds, serviceListing },
      dispatch,
      getState,
      sdk
    );
  }

  if ([CUSTOMER_CANCEL_TRANSITION, DEAL_CUSTOMER_CANCEL_TRANSITION].includes(transitionName)) {
    return runSelfServeCancellation({ txId, transitionName, params, serviceListing }, dispatch, getState, sdk);
  }

  // Confirm Prompt
  const { disputeAmountByMerchant, isFullRefund, refundPercentage } = params?.protectedData || {};
  const updatedAmount = (() => {
    if (isFullRefund || refundPercentage > 0) {
      const stateNow = getState();
      const txRefNow = stateNow?.TransactionPage?.transactionRef;
      const txEntities = getMarketplaceEntities(stateNow, txRefNow ? [txRefNow] : []);
      const currentTx = txEntities[0];
      const payoutTotalAmount = currentTx?.attributes?.payoutTotal?.amount;
      const payoutTotal = payoutTotalAmount != null ? payoutTotalAmount / 100 : 0;
      // Full refund = 100% of payout; percentage refund = that % of the same base.
      const factor = isFullRefund ? 1 : Number(refundPercentage) / 100;
      return Number(payoutTotal * factor || 0).toFixed(2);
    }

    return Number(disputeAmountByMerchant || 0).toFixed(2);
  })();

  const TRANSITION_CONFIRM_MESSAGES = {
    'transition/received-from-disputed': intl?.formatMessage(
      { id: 'TransactionPage.confirmResolveDispute' },
      { amount: updatedAmount }
    ),
    'transition/provider-mark-received-from-disputed': intl?.formatMessage(
      { id: 'TransactionPage.confirmResolveDispute' },
      { amount: updatedAmount }
    ),
    'transition/cancel-from-disputed': intl?.formatMessage({
      id: 'TransactionPage.confirmCancelDispute',
    }),
    'transition/provider-mark-cancel-from-disputed': intl?.formatMessage({
      id: 'TransactionPage.confirmCancelDispute',
    }),
  };

  const confirmMessage = TRANSITION_CONFIRM_MESSAGES[transitionName];
  if (confirmMessage && !confirm(confirmMessage)) {
    return Promise.resolve('cancelled');
  }

  // Prepare params
  const isDealBooking = processName === DEAL_PROCESS_NAME;
  const isDealPurchase = processName === DEAL_PURCHASE_PROCESS_NAME;
  const {
    bookingStart,
    bookingEnd,
    bookingDisplayStart,
    bookingDisplayEnd,
    seats,
    ...paramsForDeal
  } = params || {};

  try {
    dispatch(transitionRequest(transitionName));

    // If merchant is accepting a dispute resolution, trigger STC refund before moving the transaction state.
    // This keeps domain state consistent: we only mark dispute as resolved once refund succeeded.
    const isDisputeAcceptTransition = [
      'transition/received-from-disputed',
      'transition/provider-mark-received-from-disputed',
    ].includes(transitionName);

    if (isDisputeAcceptTransition) {
      const disputeAmount =
        disputeAmountByMerchant != null ? parseFloat(disputeAmountByMerchant) : NaN;
      if (process.env.NODE_ENV !== 'production') {
        console.log('[STC][DisputeResolve] Resolve transition', {
          txId: txId?.uuid,
          transitionName,
          disputeAmountByMerchant: params?.protectedData?.disputeAmountByMerchant,
          disputeAmount,
          isDisputeAmountValid: !Number.isNaN(disputeAmount) && disputeAmount > 0,
          isFullRefund: !!isFullRefund,
        });
      }

      if (!!isFullRefund || refundPercentage > 0 || (!Number.isNaN(disputeAmount) && disputeAmount > 0)) {
        const stateNow = getState();
        const currentUserId = stateNow?.user?.currentUser?.id?.uuid;
        const txRefNow = stateNow?.TransactionPage?.transactionRef;
        const txEntities = getMarketplaceEntities(stateNow, txRefNow ? [txRefNow] : []);
        const currentTx = txEntities[0];
        const providerId = currentTx?.provider?.id?.uuid;
        const paymentRefFromState = stateNow?.TransactionPage?.paymentReference || null;

        if (process.env.NODE_ENV !== 'production') {
          console.log('[STC][DisputeResolve] Actor check', {
            currentUserId,
            providerId,
            isProvider: !!(currentUserId && providerId && currentUserId === providerId),
            paymentReferenceFromState: paymentRefFromState || '(none)',
          });
        }

        // Only the merchant/provider triggers the refund at this stage.
        if (currentUserId && providerId && currentUserId === providerId) {
          let paymentId = paymentRefFromState;
          let paymentType = stateNow?.TransactionPage?.payment_type || null;

          // Fallback: resolve payment reference by Sharetribe tx id.
          if (!paymentId) {
            if (process.env.NODE_ENV !== 'production') {
              console.log('[DisputeResolve] Resolving paymentId via API', { txId: txId?.uuid });
            }
            const pr = await getPaymentReference(txId?.uuid);
            paymentId = pr?.paymentReference || null;
            paymentType = pr?.payment_type || null;
          }

          if (!paymentId) {
            log.error(new Error('Missing payment reference'), '[DisputeResolve] No paymentId', {
              txId: txId?.uuid,
            });
            throw new Error('Missing payment reference; cannot process refund');
          }

          if (process.env.NODE_ENV !== 'production') {
            console.log('[DisputeResolve] Calling refund', {
              paymentId,
              paymentType,
              amount: isFullRefund ? '(omitted for full refund)' : disputeAmount,
              txId: txId?.uuid,
            });
          }
          // Refund first; if it fails, do not transition.
          if (paymentType === 'tap') {
            await dispatch(
              refundTapPayment(
                isFullRefund
                  ? { chargeId: paymentId }
                  : refundPercentage > 0
                    ? { chargeId: paymentId, refundPercentage }
                    : { chargeId: paymentId, amount: disputeAmount }
              )
            );
          } else {
            // Default to STC or handle specifically
            await dispatch(
              refundStcPayment(
                isFullRefund
                  ? { paymentId }
                  : refundPercentage > 0
                    ? { paymentId, refundPercentage }
                    : { paymentId, amount: disputeAmount }
              )
            );
          }

          if (process.env.NODE_ENV !== 'production') {
            console.log('[DisputeResolve] Refund succeeded, proceeding to transition', {
              paymentId,
              txId: txId?.uuid,
            });
          }
        }
      }
    }

    // Handle child transactions if they exist
    if ((isDealBooking || isDealPurchase) && dealChildTransactionIds?.length > 0) {
      try {
        await Promise.all(
          dealChildTransactionIds.map(async childTxId => {
            const res = await sdk.transactions.transition(
              {
                id: childTxId,
                transition:
                  DEAL_ACCEPT_RESCHEDULE_TRANSITION === transitionName
                    ? ACCEPT_RESCHEDULE_TRANSITION
                    : transitionName,
                params,
              },
              { expand: true }
            );
            console.log(`Child transaction ${childTxId} transitioned successfully`, res);
            return res;
          })
        );
      } catch (err) {
        console.error('Error during child transaction transitions:', err);
        dispatch(transitionError(storableError(err)));
        log.error(err, 'child-transition-failed', { txId, transition: transitionName });
        throw err;
      }
    }
    // Main transition request
    const response = await sdk.transactions.transition(
      {
        id: txId,
        transition: transitionName,
        params: DEAL_ACCEPT_RESCHEDULE_TRANSITION === transitionName ? paramsForDeal : params,
      },
      {
        include: ['listing', 'listing.images', 'customer', 'provider'],
        ...getNotificationImageQueryParams(),
        expand: true,
      }
    );
    const user = getState()?.user?.currentUser;
    await sendNotification(response, user, transitionName, serviceListing);
    dispatch(addMarketplaceEntities(response));
    dispatch(transitionSuccess());
    dispatch(fetchCurrentUserNotifications());

    // Record who cancelled the dispute in Sharetribe metadata (fire-and-forget)
    if (
      transitionName === 'transition/cancel-from-disputed' ||
      transitionName === 'transition/provider-mark-cancel-from-disputed'
    ) {
      onRecordDisputeCancelled({ transactionId: txId }).catch(() => {});
    }

    // Delayed updates for automatic transitions
    refreshTransactionEntity(sdk, txId, dispatch);

    if (REQUEST_RESCHEDULE_TRANSITION === transitionName) {
      trackEventsMixpanel(Events.CUSTOMER_BOOKING_COMPLETED, {
        action_type: mixPanelEventsData.CUSTOMER_BOOKING_RESCHEDULE,
        screen: mixPanelEventsScreens.TRANSACTION_PAGE,
        user_type: 'customer',
        user_id: user?.id?.uuid,
        ...buildTransactionValueProps(response?.data?.data, {
          role: 'customer',
          listingTitle: serviceListing?.attributes?.title,
        }),
      });
    }

    // Dispute raise/resolve tracking. Every dispute transition converges
    // here, so one map covers accept and decline for all four processes --
    // nothing fires for transitions outside it (would flood the project).
    const role = user?.id?.uuid === currentTx?.provider?.id?.uuid ? 'merchant' : 'customer';
    if (transitionName === 'transition/dispute') {
      trackEventsMixpanel(Events.DISPUTE_RAISED, {
        action_type: 'dispute_raise',
        screen: mixPanelEventsScreens.TRANSACTION_PAGE,
        user_type: role,
        user_id: user?.id?.uuid,
        raised_by: 'customer',
        dispute_reason: params?.protectedData?.disputeReason ?? null,
        ...buildTransactionValueProps(currentTx, { role, listingTitle: serviceListing?.attributes?.title }),
      });
    } else if (
      ['transition/received-from-disputed', 'transition/provider-mark-received-from-disputed'].includes(
        transitionName
      )
    ) {
      trackEventsMixpanel(Events.DISPUTE_RESOLVED, {
        action_type: 'dispute_accepted',
        screen: mixPanelEventsScreens.TRANSACTION_PAGE,
        user_type: 'provider',
        user_id: user?.id?.uuid,
        resolved_by: 'merchant',
        outcome: 'accepted',
        dispute_amount_by_merchant_sar: Number(disputeAmountByMerchant) || null,
        refund_type: isFullRefund ? 'full' : refundPercentage > 0 ? 'half' : null,
        ...buildTransactionValueProps(currentTx, { role, listingTitle: serviceListing?.attributes?.title }),
      });
    } else if (
      ['transition/cancel-from-disputed', 'transition/provider-mark-cancel-from-disputed'].includes(
        transitionName
      )
    ) {
      trackEventsMixpanel(Events.DISPUTE_RESOLVED, {
        action_type: 'dispute_declined',
        screen: mixPanelEventsScreens.TRANSACTION_PAGE,
        user_type: 'provider',
        user_id: user?.id?.uuid,
        resolved_by: 'merchant',
        outcome: 'declined',
        ...buildTransactionValueProps(currentTx, { role, listingTitle: serviceListing?.attributes?.title }),
      });
    }

    // Reload on certain transitions (if necessary)
    if (
      [ACCEPT_RESCHEDULE_TRANSITION, DEAL_ACCEPT_RESCHEDULE_TRANSITION].includes(transitionName)
    ) {
      window.location.reload(); // Uncomment if reload is necessary
    }

    return response;
  } catch (e) {
    dispatch(transitionError(storableError(e)));
    log.error(e, `${transitionName}-failed`, {
      txId,
      transition: transitionName,
      message: e?.message,
      isDisputeResolve: [
        'transition/received-from-disputed',
        'transition/provider-mark-received-from-disputed',
      ].includes(transitionName),
    });
    throw e;
  }
};

// Fixed/Hourly keep Sharetribe's native messaging; Negotiated reads/writes
// through server/api/messages instead (see docs/transaction-messages-plan.md).
// The transaction is resolved from the store rather than threaded through
// component props, so TransactionPage.js/ActivityFeed.js don't need to change.
const getLoadedTransaction = getState => {
  const state = getState();
  const ref = state.TransactionPage.transactionRef;
  const transactions = getMarketplaceEntities(state, ref ? [ref] : []);
  return transactions[0];
};

const isNegotiatedTransaction = tx =>
  tx?.attributes?.protectedData?.transactionModel === NEGOTIATED_TRANSACTION_MODEL;

/** Longest message excerpt carried in a push body. Matches both apps. */
const MESSAGE_PREVIEW_LENGTH = 80;

const buildMessagePreview = content => {
  const collapsed = (content ?? '').replace(/\s+/g, ' ').trim();
  return collapsed.length > MESSAGE_PREVIEW_LENGTH
    ? `${collapsed.slice(0, MESSAGE_PREVIEW_LENGTH - 1)}…`
    : collapsed;
};

/**
 * Push for a negotiation event, built from the transaction already in state.
 *
 * The negotiation paths don't go through makeTransition — quotes use the
 * privileged route and accept/reject use a bare SDK transition — so none of
 * them get its notification for free. This is that missing half, and it reads
 * the loaded entity rather than a transition response so the chat's message
 * send (which has no transition at all) can use it too.
 *
 * Whoever is NOT the current user receives it; a missing template, party or
 * transaction means no push rather than a failed request.
 */
const notifyNegotiation = (getState, { label, extra = {} }) => {
  const template = getTemplate(label);
  const tx = getLoadedTransaction(getState);
  const currentUser = getState()?.user?.currentUser;
  const senderUserId = currentUser?.id?.uuid;

  if (!template || !tx?.id?.uuid || !senderUserId) {
    return Promise.resolve();
  }

  const isProvider = senderUserId === tx?.provider?.id?.uuid;
  const receiverUserId = isProvider ? tx?.customer?.id?.uuid : tx?.provider?.id?.uuid;
  if (!receiverUserId) {
    return Promise.resolve();
  }

  const listing = tx?.listing;
  const { collapseId, ...extraCustomData } = extra;
  const customData = {
    image: getImageUrlFromListingEntity(listing),
    order_type: 'Booking',
    transactionId: tx.id.uuid,
    listingId: listing?.id?.uuid || '',
    targetScreen: 'NegotiationChat',
    item_name: listing?.attributes?.title || '',
    [isProvider ? 'merchant_name' : 'customer_name']:
      currentUser?.attributes?.profile?.displayName || '',
    label,
    ...extraCustomData,
  };

  return onSendPushNotification({
    userRole: isProvider ? 'merchant' : 'customer',
    receiverUserIds: [receiverUserId],
    senderUserId,
    template_id: template,
    notificationData: customData,
    customData,
    ...(collapseId ? { collapse_id: collapseId } : {}),
  }).catch(e => console.log('notifyNegotiation error', e));
};

// Maps a transaction_messages row into the same shape ActivityFeed already
// consumes from sdk.messages.query, so it never learns which backend it's
// rendering. messageType/structuredPayload ride along on attributes so
// ActivityFeed can render quote/counter_offer rows as a card instead of a
// text bubble.
const mapDbMessageToEntity = (dbMessage, tx) => {
  const sender =
    dbMessage.senderType === 'customer'
      ? tx?.customer
      : dbMessage.senderType === 'provider'
        ? tx?.provider
        : null;

  return {
    id: new UUID(dbMessage.id),
    type: 'message',
    attributes: {
      content: dbMessage.content,
      createdAt: new Date(dbMessage.createdAt),
      messageType: dbMessage.messageType,
      structuredPayload: dbMessage.structuredPayload,
      attachments: dbMessage.attachments,
    },
    sender,
  };
};

const fetchNegotiatedMessages = (txId, page, dispatch, getState) => {
  const state = getState().TransactionPage;
  const oldestLoaded =
    page > 1 && state.messages.length > 0
      ? state.messages.reduce(
          (min, m) => (m.attributes.createdAt < min ? m.attributes.createdAt : min),
          state.messages[0].attributes.createdAt
        )
      : null;

  return fetchTransactionMessages(txId.uuid, {
    before: oldestLoaded ? oldestLoaded.toISOString() : undefined,
    limit: MESSAGES_PAGE_SIZE,
  })
    .then(({ messages: rows }) => {
      const tx = getLoadedTransaction(getState);
      const entities = rows.map(row => mapDbMessageToEntity(row, tx));

      // No cheap "total" query for a cursor-paginated table -- approximate
      // "there might be more" by whether this page came back full.
      const hasMoreOlder = rows.length === MESSAGES_PAGE_SIZE;
      const pagination = {
        totalItems: state.totalMessages + entities.length,
        totalPages: hasMoreOlder ? page + 1 : page,
        page,
      };
      dispatch(fetchMessagesSuccess(entities, pagination));
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  dispatch(fetchMessagesRequest());

  const tx = getLoadedTransaction(getState);
  if (isNegotiatedTransaction(tx)) {
    return fetchNegotiatedMessages(txId, page, dispatch, getState);
  }

  const paging = { page, perPage: MESSAGES_PAGE_SIZE };

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().TransactionPage.totalMessages;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  const tx = getLoadedTransaction(getState);
  if (isNegotiatedTransaction(tx)) {
    return sendTransactionMessage({ transactionId: txId.uuid, content: message })
      .then(({ message: dbMessage }) => {
        const entity = mapDbMessageToEntity(dbMessage, tx);
        const state = getState().TransactionPage;
        dispatch(
          fetchMessagesSuccess([entity], {
            totalItems: state.totalMessages + 1,
            totalPages: state.totalMessagePages || 1,
            page: 1,
          })
        );
        // Neither app polls the thread, so a message sent from here is
        // invisible on the other side until it is reopened.
        const isProvider = getState()?.user?.currentUser?.id?.uuid === tx?.provider?.id?.uuid;
        notifyNegotiation(getState, {
          label: isProvider
            ? NEGOTIATION_MESSAGE_LABELS.fromProvider
            : NEGOTIATION_MESSAGE_LABELS.fromCustomer,
          extra: {
            message_preview: buildMessagePreview(message),
            collapseId: `negotiation-${txId.uuid}`,
          },
        });
        dispatch(sendMessageSuccess());
        return entity.id;
      })
      .catch(e => {
        dispatch(sendMessageError(storableError(e)));
        throw e;
      });
  }

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;

      // We fetch the first page again to add sent message to the page data
      // and update possible incoming messages too.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => dispatch(sendMessageSuccess()));
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// Negotiation transitions -- these are real process.edn states (offer-pending,
// customer-offer-pending, offer-accepted), not just chat events. See the
// negotiation sub-graph in ext/transaction-processes/default-booking/process.edn
// and transactions/transactionProcessBooking.js.

// server/api-util/lineItems.js reads this exactly like dealPricingOverride
// for the base (commission-bearing) line -- a flat, already-agreed amount
// that bypasses booking-duration pricing entirely, since a quote/counter-offer
// happens before any dates are picked. Unlike dealPricingOverride though, the
// base amount here is Labour/Service ONLY: materials/travel/inspection ride
// along as pass-through `lineItems` that the server turns into their own
// extraLineItems, excluded from commission -- matching what the quote card
// already tells the merchant ("Commission applies to Labour/Service only").
const buildNegotiatedPricingOverride = (lineItems, currency) => {
  const labourAmount = lineItems.find(li => li.code === 'labour')?.amount || 0;
  const totalAmount = lineItems.reduce((sum, li) => sum + (Number(li.amount) || 0), 0);
  const passThroughLineItems = lineItems
    .filter(li => li.code !== 'labour')
    .map(li => ({ code: li.code, amount: li.amount }));

  return {
    quantity: 1,
    unitPrice: new Money(labourAmount, currency),
    amount: totalAmount,
    currency,
    lineItems: passThroughLineItems,
  };
};

// customer-make-offer-after-inquiry / provider-make-counter-offer /
// customer-make-counter-offer all carry :action/privileged-set-line-items,
// so (unlike customer-accept-offer/customer-reject-offer, which are plain)
// they must go through transition-privileged.js, not the client SDK directly.
const fireNegotiationPriceTransition = (transaction, transitionName, lineItems, currency) =>
  transitionPrivileged({
    isSpeculative: false,
    orderData: {
      negotiatedPricingOverride: buildNegotiatedPricingOverride(lineItems, currency),
    },
    bodyParams: {
      id: transaction.id,
      transition: transitionName,
      params: { listingId: transaction.listing?.id },
    },
    queryParams: { include: ['listing', 'provider', 'customer'], expand: true },
  });

const postQuoteMessage = (dispatch, getState, txId, structuredPayload) =>
  sendTransactionQuote({ transactionId: txId.uuid, structuredPayload }).then(({ message: dbMessage }) => {
    const tx = getLoadedTransaction(getState);
    const entity = mapDbMessageToEntity(dbMessage, tx);
    const state = getState().TransactionPage;
    dispatch(
      fetchMessagesSuccess([entity], {
        totalItems: state.totalMessages + 1,
        totalPages: state.totalMessagePages || 1,
        page: 1,
      })
    );
    return entity;
  });

// Provider sends/revises a quote. `transitionName` is chosen by the caller
// from the transaction's current process state: 'inquiry' can only reach
// offer-pending via make-offer-after-inquiry; customer-offer-pending can
// only be answered via provider-make-counter-offer. There is no transition
// for revising a quote the customer hasn't responded to yet.
export const sendQuote = (transaction, transitionName, structuredPayload) => (dispatch, getState) => {
  dispatch(sendMessageRequest());

  return fireNegotiationPriceTransition(
    transaction,
    transitionName,
    structuredPayload.lineItems,
    structuredPayload.currency
  )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return postQuoteMessage(dispatch, getState, transaction.id, structuredPayload);
    })
    .then(entity => {
      // After the message: an offer the customer can open but that has no card
      // yet would be worse than a slightly later push.
      notifyNegotiation(getState, {
        label: getLabel(transitionName),
        extra: negotiationAmountData(structuredPayload),
      });
      dispatch(sendMessageSuccess());
      return entity.id;
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

/** Amount fields the offer/counter templates substitute, in major units. */
const negotiationAmountData = structuredPayload => ({
  amount: String((Number(structuredPayload?.total) || 0) / 100),
  currency: structuredPayload?.currency || '',
});

// Customer counters with their own itemized breakdown. Only valid from
// offer-pending. Same shape as sendQuote's structuredPayload -- the customer
// can adjust any line item (labour, materials, travel, inspection), not just
// the total, so it needs the same fully-itemized transition payload.
export const sendCounterOffer = (transaction, structuredPayload) => (dispatch, getState) => {
  dispatch(sendMessageRequest());

  return fireNegotiationPriceTransition(
    transaction,
    'transition/customer-make-counter-offer',
    structuredPayload.lineItems,
    structuredPayload.currency
  )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return postQuoteMessage(dispatch, getState, transaction.id, structuredPayload);
    })
    .then(entity => {
      notifyNegotiation(getState, {
        label: getLabel('transition/customer-make-counter-offer'),
        extra: negotiationAmountData(structuredPayload),
      });
      dispatch(sendMessageSuccess());
      return entity.id;
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

// Plain transitions -- no actions, no pricing, safe to call through the
// ordinary client SDK exactly like any other non-privileged transition.
export const acceptOffer = txId => dispatch =>
  dispatch(makeTransitionWithoutPricing(txId, 'transition/customer-accept-offer'));

export const rejectOffer = txId => dispatch =>
  dispatch(makeTransitionWithoutPricing(txId, 'transition/customer-reject-offer'));

// Provider accepts the customer's counter-offer (only valid from
// customer-offer-pending).
export const providerAcceptOffer = txId => dispatch =>
  dispatch(makeTransitionWithoutPricing(txId, 'transition/provider-accept-offer'));

const makeTransitionWithoutPricing = (txId, transitionName) => async (dispatch, getState, sdk) => {
  dispatch(transitionRequest(transitionName));
  try {
    const response = await sdk.transactions.transition(
      { id: txId, transition: transitionName, params: {} },
      { include: ['listing', 'provider', 'customer'], expand: true }
    );
    dispatch(addMarketplaceEntities(response));
    // After the entity lands, so the push reads the post-transition state.
    // getLabel returns undefined for anything without a template, and
    // notifyNegotiation no-ops on that.
    notifyNegotiation(getState, { label: getLabel(transitionName) });
    dispatch(transitionSuccess());
    return response;
  } catch (e) {
    dispatch(transitionError(storableError(e)));
    throw e;
  }
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch(e => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(id, params, role, dispatch, sdk);
      } else {
        dispatch(sendReviewError(storableError(e)));

        // Rethrow so the page can track whether the sending failed, and
        // keep the message in the form for a retry.
        throw e;
      }
    });
};

export const sendReview = (tx, transitionOptionsInfo, params, config) => (
  dispatch,
  getState,
  sdk
) => {


  // console.log("tx", tx)
  // console.log("transitionOptionsInfo", transitionOptionsInfo)
  // return
  
  const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
  dispatch(sendReviewRequest());
  const { reviewRating, reviewContent, images, listingId, serviceId, staffValues } = params;

  const user = getState().user?.currentUser;
  const userId = user?.id?.uuid;
  const providerId = tx?.provider?.id?.uuid;
  const customerId = tx?.customer?.id?.uuid;
  const transactionId = tx?.id?.uuid;

  const userType = user?.attributes?.profile?.publicData?.userType;
  const reviewerType = userType === 'customer' ? 'customer' : 'merchant';
  const processName = resolveLatestProcessName(tx);
  const parentDealTxIdRaw = tx?.attributes?.protectedData?.parentTransactionId;
  const parentDealTxId =
    typeof parentDealTxIdRaw === 'string'
      ? parentDealTxIdRaw
      : parentDealTxIdRaw?.uuid || parentDealTxIdRaw?.id?.uuid || null;
  const dealTxId =
    parentDealTxId ||
    (processName === DEAL_PROCESS_NAME || processName === DEAL_PURCHASE_PROCESS_NAME
      ? transactionId
      : null);

  const reviewParams = {
    listingId: serviceId || listingId, //use serviceId if its a service or serviceDeal tx
    reviewerId: userId,
    reviewerType: reviewerType,
    otherUserId: userId === providerId ? customerId : providerId,
    content: reviewContent,
    rating: reviewRating,
    images: images || [],
    stafId: null,
    txId: transactionId,
    dealTxId,
  };

  const { staffReviewRating, staffReviewImages, staffReviewContent } = staffValues || {};

  const staffReviewParams = {
    listingId: serviceId,
    reviewerId: userId,
    reviewerType: reviewerType,
    // otherUserId: userId === providerId ? customerId : providerId,
    otherUserId: null,
    stafId: listingId, //listingId is staffId itself becoz it's a staff tx
    content: staffReviewContent,
    rating: Number.parseInt(staffReviewRating, 10),
    images: staffReviewImages || [],
    txId: transactionId,
  };
  // trackEventsMixpanel(mixPanelEventsConstants.RATING_AND_REVIEW, {
  //   screen: mixPanelEventsScreens.TRANSACTION_PAGE,
  //   action_type:
  //     userType === 'customer' ? mixPanelEventsData.RATE_MERCHANT : mixPanelEventsData.RATE_CUSTOMER,
  //   user_id: userId,
  //   user_type: userType === 'customer' ? 'Customer' : 'Provider',
  // });
  const state = getState();
  const txServiceId = tx?.attributes?.protectedData?.serviceId;
  const resolvedServiceId = serviceId || txServiceId || null;
  const notificationListing =
    (resolvedServiceId && getListing(state, resolvedServiceId)) ||
    (listingId && getListing(state, listingId)) ||
    (tx?.listing?.id?.uuid && getListing(state, tx?.listing?.id?.uuid)) ||
    tx?.listing;
  const fallbackReviewImageUrl = getImageUrlFromListingEntity(notificationListing);
  const fallbackReviewListingTitle = notificationListing?.attributes?.title || '';
  const notificationListingId =
    resolvedServiceId ||
    listingId ||
    notificationListing?.id?.uuid ||
    notificationListing?.id ||
    tx?.listing?.id?.uuid ||
    null;


  const reviewNotificationMetaPromise =
    fallbackReviewImageUrl || !notificationListingId
      ? Promise.resolve({
          listing: notificationListing,
          imageUrl: fallbackReviewImageUrl,
          listingTitle: fallbackReviewListingTitle,
        })
      : sdk.listings
          .show(
            {
              id: notificationListingId,
              include: ['images'],
              ...getNotificationImageQueryParams(),
            },
            { expand: true }
          )
          .then(response => {
            const fetchedListing = response?.data?.data;
            const fetchedIncluded = response?.data?.included || [];
            const fetchedImageUrl =
              getImageUrlFromIncludedListing(fetchedListing, fetchedIncluded) ||
              getImageUrlFromListingEntity(fetchedListing);

            return {
              listing: fetchedListing || notificationListing,
              imageUrl: fetchedImageUrl || fallbackReviewImageUrl,
              listingTitle:
                fetchedListing?.attributes?.title ||
                fallbackReviewListingTitle ||
                '',
            };
          })
          .catch(() => ({
            listing: notificationListing,
            imageUrl: fallbackReviewImageUrl,
            listingTitle: fallbackReviewListingTitle,
          }));

  return reviewNotificationMetaPromise.then(reviewNotificationMeta => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('[NotificationDebug][REVIEW_RECEIVED] resolved image before send', {
        transactionId: tx?.id?.uuid,
        resolvedServiceId,
        listingId: reviewNotificationMeta?.listing?.id?.uuid || notificationListingId,
        hasListingImages: Array.isArray(reviewNotificationMeta?.listing?.images)
          ? reviewNotificationMeta.listing.images.length
          : 0,
        image: reviewNotificationMeta?.imageUrl || '',
      });
      console.log('[NotificationDebug][REVIEW_RECEIVED] review params', {
        txId: transactionId,
        dealTxId,
        listingId: reviewParams.listingId,
      });
    }

    return onAddReview(reviewParams)
    .then(() => {

      sendReviewReceivedNotification({
        tx,
        listing: reviewNotificationMeta?.listing || notificationListing,
        senderUser: user,
        receiverUserId: reviewParams.otherUserId,
        reviewRating,
        reviewContent,
        imageUrl: reviewNotificationMeta?.imageUrl || fallbackReviewImageUrl,
        listingTitle: reviewNotificationMeta?.listingTitle || fallbackReviewListingTitle,
      });

      // If it's a staff review, call onAddReview with staffReviewParams as well
      if (staffReviewRating && staffReviewContent && serviceId) {
        return onAddReview(staffReviewParams)
          .then(() => {
            // trackEventsMixpanel(mixPanelEventsConstants.RATING_AND_REVIEW, {
            //   screen: mixPanelEventsScreens.TRANSACTION_PAGE,
            //   action_type: mixPanelEventsData.COMPLETE_REVIEW,
            //   user_id: userId,
            //   user_type: userType === 'customer' ? 'Customer' : 'Provider',
            // });
            return hasOtherPartyReviewedFirst
              ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
              : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
          })
          .catch(err => {
            console.log('Failed adding staff review', err);
          });
      } else {
        // If it's not a staff review, continue the regular flow
        return hasOtherPartyReviewedFirst
          ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
          : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config);
      }
    })
    .catch(err => {
      console.log('Failed adding review', err);
    });
  });
};

const isNonEmpty = value => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = id => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then(res => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch(e => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const fileUploadAWS = file => async (dispatch, getState, sdk) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${apiBaseUrl()}/api/upload-file-to-aws`, {
      method: 'POST',
      body: formData,
    });

    // Check if the response is okay (status code 200-299)
    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    console.error('Error uploading file:', error);
  }
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => async (dispatch, getState) => {
  const txId = new UUID(params.id);
  const pageState = getState().TransactionPage;
  const txRef = pageState.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(pageState, isNonEmpty);
  dispatch(setInitialValues(initialValues));

  // fetchMessages needs to know transactionModel (Negotiated vs not) to pick
  // its data source, and that only exists once the transaction has loaded --
  // so this can no longer run in parallel with it like the other two below.
  await dispatch(fetchTransaction(txId, txRole, config));

  const result = await Promise.all([
    dispatch(fetchMessages(txId, 1, config)),
    dispatch(fetchNextTransitions(txId)),
  ]);

  // Timeline (payin pending/available): fire-and-forget, requires provider = merchantId.
  const state = getState();
  const ref = state.TransactionPage.transactionRef;
  const transactions = getMarketplaceEntities(state, ref ? [ref] : []);
  const tx = transactions[0];
  const merchantId = tx?.provider?.id?.uuid;
  if (params.id && merchantId) {
    dispatch(fetchTimeline(params.id, merchantId));
  }

  return result;
};
